import type { LucideIcon } from "lucide-react";
import { Building2, GraduationCap, Users, Server } from "lucide-react";
import logo1 from "../../assets/schools/african-graduate.webp";
import logo2 from "../../assets/schools/chavara-cmi-school.webp";
import logo3 from "../../assets/schools/airforce-school.webp";
import logo4 from "../../assets/schools/delhi-school.webp";
import logo5 from "../../assets/schools/father-agnel-school.webp";
import logo6 from "../../assets/schools/lotus-valley-school.webp";
import logo1Small from "../../assets/schools/african-graduate-64.webp";
import logo2Small from "../../assets/schools/chavara-cmi-school-64.webp";
import logo3Small from "../../assets/schools/airforce-school-64.webp";
import logo4Small from "../../assets/schools/delhi-school-64.webp";
import logo5Small from "../../assets/schools/father-agnel-school-64.webp";
import logo6Small from "../../assets/schools/lotus-valley-school-64.webp";
import logo1Medium from "../../assets/schools/african-graduate-80.webp";
import logo2Medium from "../../assets/schools/chavara-cmi-school-80.webp";
import logo3Medium from "../../assets/schools/airforce-school-80.webp";
import logo4Medium from "../../assets/schools/delhi-school-80.webp";
import logo5Medium from "../../assets/schools/father-agnel-school-80.webp";
import logo6Medium from "../../assets/schools/lotus-valley-school-80.webp";

const formatName = (name: string) => {
    return name
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

// ─── Top International / Notable Schools in Ethiopia ─────────────────────────
const schools = [
    { logo: logo1, logoSmall: logo1Small, logoMedium: logo1Medium, name: formatName("african-graduate") },
    { logo: logo2, logoSmall: logo2Small, logoMedium: logo2Medium, name: formatName("chavara-cmi-public-school") },
    { logo: logo3, logoSmall: logo3Small, logoMedium: logo3Medium, name: formatName("airforce-school") },
    { logo: logo4, logoSmall: logo4Small, logoMedium: logo4Medium, name: formatName("delhi-public-school") },
    { logo: logo5, logoSmall: logo5Small, logoMedium: logo5Medium, name: formatName("father-agnel-school") },
    { logo: logo6, logoSmall: logo6Small, logoMedium: logo6Medium, name: formatName("lotus-valley-school") },
];

// One duplicate is enough for a seamless loop and keeps the DOM compact.
const scrollList = [...schools, ...schools];

// ─── Stats ────────────────────────────────────────────────────────────────────
const stats = [
    { icon: Building2, target: 23, suffix: "+", label: "Schools" },
    { icon: GraduationCap, target: 120781, suffix: "+", label: "Students Managed" },
    { icon: Users, target: 500, suffix: "+", label: "Teachers" },
    { icon: Server, target: 99.9, suffix: "%", label: "System Uptime", isDecimal: true },
];

function StatCard({ icon: Icon, target, suffix, label, isDecimal = false }: {
    icon: LucideIcon; target: number; suffix: string; label: string; isDecimal?: boolean;
}) {
    const value = isDecimal ? target.toFixed(1) : target.toLocaleString();

    return (
        <div className="flex flex-col items-center text-center gap-2">
            <div className="mb-2 text-primary">
                <Icon className="w-7 h-7" strokeWidth={1.5} />
            </div>
            <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                {value}{suffix}
            </p>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                {label}
            </p>
        </div>
    );
}

const StatsSection = () => (
    <section className="py-12 border-y border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950">
        {/* Marquee keyframe */}
        <style dangerouslySetInnerHTML={{ __html: `
      @keyframes stats-marquee {
        from { transform: translateX(0); }
        to   { transform: translateX(-50%); }
      }
      .stats-marquee-track {
        animation: stats-marquee 30s linear infinite;
      }
      .stats-marquee-track:hover {
        animation-play-state: paused;
      }
    ` }} />

        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
            {/* Label */}
            <p className="text-center text-sm font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-widest mb-6">
                Trusted by institutions across Ethiopia
            </p>

            {/* ─── Scrolling school logos ──────────────────────────────────────── */}
            <div className="mb-10 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
                <div className="flex stats-marquee-track gap-4 w-max">
                    {scrollList.map((s, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-3 px-5 py-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex-shrink-0 select-none"
                        >
                            {/* Logo image */}
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-white p-1">
                                <img
                                    src={s.logo}
                                    srcSet={`${s.logoSmall} 64w, ${s.logoMedium} 80w, ${s.logo} 128w`}
                                    sizes="40px"
                                    alt={s.name}
                                    width="128"
                                    height="128"
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <span className="text-sm font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap max-w-[160px] truncate" title={s.name}>
                                {s.name}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ─── Stats ───────────────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {stats.map((stat) => (
                    <StatCard key={stat.label} {...stat} />
                ))}
            </div>
        </div>
    </section>
);

export default StatsSection;
